/**
 * Secure logging utility that prevents sensitive information from being exposed in production
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4
}

class SecureLogger {
  private logLevel: LogLevel;
  private isProduction: boolean;

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.logLevel = this.isProduction ? LogLevel.ERROR : LogLevel.DEBUG;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private sanitizeData(data: any): any {
    if (this.isProduction) {
      // In production, remove sensitive fields
      if (typeof data === 'object' && data !== null) {
        const sanitized = { ...data };
        const sensitiveFields = [
          'password', 'token', 'authorization', 'authToken', 'apiKey', 
          'secret', 'key', 'credentials', 'auth', 'bearer'
        ];
        
        sensitiveFields.forEach(field => {
          if (sanitized[field]) {
            sanitized[field] = '[REDACTED]';
          }
        });

        // Remove URL details in production
        if (sanitized.url) {
          sanitized.url = '[REDACTED_URL]';
        }
        if (sanitized.baseURL) {
          sanitized.baseURL = '[REDACTED_BASE_URL]';
        }
        if (sanitized.endpoint) {
          sanitized.endpoint = '[REDACTED_ENDPOINT]';
        }

        return sanitized;
      }
    }
    return data;
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`[DEBUG] ${message}`, data ? this.sanitizeData(data) : '');
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`[INFO] ${message}`, data ? this.sanitizeData(data) : '');
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, data ? this.sanitizeData(data) : '');
    }
  }

  error(message: string, error?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, error ? this.sanitizeData(error) : '');
    }
  }

  // API-specific logging methods
  apiRequest(endpoint: string, method: string, data?: any): void {
    if (this.isProduction) {
      this.info(`API ${method} request to ${endpoint.replace(/\/api\/.*/, '/api/[REDACTED]')}`);
    } else {
      this.debug(`API ${method} request`, { endpoint, method, data });
    }
  }

  apiResponse(endpoint: string, status: number, data?: any): void {
    if (this.isProduction) {
      this.info(`API response ${status} from ${endpoint.replace(/\/api\/.*/, '/api/[REDACTED]')}`);
    } else {
      this.debug(`API response ${status}`, { endpoint, status, data });
    }
  }

  apiError(endpoint: string, error: any): void {
    this.error(`API error from ${endpoint.replace(/\/api\/.*/, '/api/[REDACTED]')}`, error);
  }
}

// Export singleton instance
export const secureLogger = new SecureLogger();
